import { z } from "zod";
import type { Args, Operation } from "../../types";
import { scoped } from "../../types";
import { crmIdParams } from "./_shared";

const sequenceSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  paused: z.number(),
  owner_actor_id: z.number().nullable(),
});

const stepSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  sequence_id: z.number(),
  sort_key: z.string(),
  day_offset: z.number(),
  channel: z.enum(["email", "sms", "call", "task"]),
  template_id: z.number().nullable(),
  instructions: z.string().nullable(),
});

const enrollmentSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  sequence_id: z.number(),
  lead_id: z.number(),
  current_step_id: z.number().nullable(),
  status: z.enum(["active", "paused", "completed", "cancelled", "failed"]),
  enrolled_at: z.string(),
  next_action_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  owner_actor_id: z.number().nullable(),
});

const list: Operation<void, void, void, { sequences: z.infer<typeof sequenceSchema>[] }> = {
  method: "GET",
  path: () => "/api/crm/sequences",
  response: z.object({ sequences: z.array(sequenceSchema) }),
};

const get: Operation<
  { id: number },
  void,
  void,
  { sequence: z.infer<typeof sequenceSchema>; steps: z.infer<typeof stepSchema>[]; enrollments: z.infer<typeof enrollmentSchema>[] }
> = {
  method: "GET",
  path: ({ id }) => `/api/crm/sequences/${id}`,
  params: crmIdParams,
  response: z.object({
    sequence: sequenceSchema,
    steps: z.array(stepSchema),
    enrollments: z.array(enrollmentSchema),
  }),
};

const create: Operation<
  void,
  void,
  { slug: string; name: string; description?: string | null },
  z.infer<typeof sequenceSchema>
> = {
  method: "POST",
  path: () => "/api/crm/sequences",
  body: z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
  }),
  response: sequenceSchema,
  invalidates: [list],
};

const stepCreate: Operation<
  { id: number },
  void,
  {
    day_offset: number;
    channel: "email" | "sms" | "call" | "task";
    template_id?: number | null;
    instructions?: string | null;
  },
  z.infer<typeof stepSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/sequences/${id}/steps`,
  params: crmIdParams,
  body: z.object({
    day_offset: z.number().int().nonnegative(),
    channel: z.enum(["email", "sms", "call", "task"]),
    template_id: z.number().int().nullable().optional(),
    instructions: z.string().nullable().optional(),
  }),
  response: stepSchema,
  // Steps live on the detail payload, not the list row.
  invalidates: [
    scoped(get, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

const enroll: Operation<
  { id: number },
  void,
  { lead_id: number },
  { enrollment: z.infer<typeof enrollmentSchema>; already_enrolled?: boolean }
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/sequences/${id}/enroll`,
  params: crmIdParams,
  body: z.object({ lead_id: z.number().int() }),
  response: z.object({
    enrollment: enrollmentSchema,
    already_enrolled: z.boolean().optional(),
  }),
  invalidates: [
    scoped(get, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

const advance: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof enrollmentSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/enrollments/${id}/advance`,
  params: crmIdParams,
  response: enrollmentSchema,
  // Path param is enrollment id; sequence id is on the result row.
  invalidates: [
    scoped(
      get,
      (_args: Args<{ id: number }, void, void>, result: { sequence_id: number }) => ({
        id: result.sequence_id,
      }),
    ),
  ],
};

const pause: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof enrollmentSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/enrollments/${id}/pause`,
  params: crmIdParams,
  response: enrollmentSchema,
  invalidates: [
    scoped(
      get,
      (_args: Args<{ id: number }, void, void>, result: { sequence_id: number }) => ({
        id: result.sequence_id,
      }),
    ),
  ],
};

const resume: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof enrollmentSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/enrollments/${id}/resume`,
  params: crmIdParams,
  response: enrollmentSchema,
  invalidates: [
    scoped(
      get,
      (_args: Args<{ id: number }, void, void>, result: { sequence_id: number }) => ({
        id: result.sequence_id,
      }),
    ),
  ],
};

const update: Operation<
  { id: number },
  void,
  { name?: string; description?: string | null; paused?: boolean },
  { ok: true }
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/sequences/${id}`,
  params: crmIdParams,
  body: z.object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    paused: z.boolean().optional(),
  }),
  response: z.object({ ok: z.literal(true) }),
  // name/paused are list columns and detail.sequence fields.
  invalidates: [
    list,
    scoped(get, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

const archive: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "DELETE",
  path: ({ id }) => `/api/crm/sequences/${id}`,
  params: crmIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [
    list,
    scoped(get, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

const stepIdParams = z.object({
  id: z.coerce.number().int(),
  stepId: z.coerce.number().int(),
});

const stepArchive: Operation<
  { id: number; stepId: number },
  void,
  void,
  { ok: true }
> = {
  method: "DELETE",
  path: ({ id, stepId }) => `/api/crm/sequences/${id}/steps/${stepId}`,
  params: stepIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [
    scoped(get, (args: Args<{ id: number; stepId: number }, void, void>) => ({
      id: args.params.id,
    })),
  ],
};

export const sequences = {
  list,
  create,
  get,
  update,
  archive,
  step_create: stepCreate,
  step_archive: stepArchive,
  enroll,
  advance,
  pause,
  resume,
};
